import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import * as Yup from 'yup';
import { FileText, Plus, Upload, User, X } from 'lucide-react';
import { Lease, LeaseInput, IntervalUnit, CoTenantsMap } from '../../types/leases';
import { CoTenantRole, LeaseObjectType, TenantKind, UnitKind } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { ParkingArea, ParkingSpot } from '../../types/parking';
import { StorageUnit } from '../../types/storageUnits';
import { Unit } from '../../types/units';
import { LevelModel } from '../../types/protocols';
import { Tenant, TenantInput } from '../../types/tenants';
import { FileAttachment } from '../../types/attachments';
import { useTenants } from '../../hooks/tenants';
import { useFeatures } from '../../hooks/useFeatures';
import { Features } from '../../api/featuresApi';
import { tenantsApi } from '../../api/tenantsApi';
import {
    DATE_ONLY_RE,
    UNIT_LEASE_OBJECT_TYPES,
    CoTenantEntry,
    LeasedObjectRef,
    coTenantEntries,
    latestLeaseForObject,
    leaseObjectTypeForUnitKind,
    parkingSpotLeaseLabel,
    storageUnitLeaseLabel,
    todayStockholm,
    unitLeaseLabel,
} from '../../Helpers/leaseUtils';
import { parkingAreaDisplayName, parkingSpotDisplayName, sortedParkingSpots } from '../../Helpers/parkingUtils';
import { storageLevelsDescending, storageUnitDisplayName, unitsOnLevel } from '../../Helpers/storageUnitUtils';
import { isUnitLeasable, unitDisplayName } from '../../Helpers/unitUtils';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { formatFileSize } from '../../Helpers/FileUtils';
import { useDragDrop } from '../../hooks/useDragDrop';
import { UploadPickerModal } from '../../Components/UploadPickerModal';
import TenantModal from '../Tenants/TenantModal';

export interface LeaseModalSubmitData {
    input: LeaseInput;
    filesToUpload: File[];
    contractsToRemove: string[];
}

interface LeaseModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (data: LeaseModalSubmitData) => Promise<void>;
    buildingKey: string;
    /** The building's parking areas (for the spot picker). */
    areas: ParkingArea[];
    /** The building's storage units (for the unit picker, grouped by level). */
    units?: StorageUnit[];
    /** The building's levels, for grouping the unit picker. */
    levels?: { [key: string]: LevelModel };
    /** The building's units (lägenheter and lokaler); only hyresrätt units are offered (plan D3). */
    rentalUnits?: Unit[];
    /** The building's leases, used to label spots and units as vacant or rented. */
    buildingLeases: Lease[];
    /** When given, the modal edits this lease; otherwise it creates one. */
    lease?: Lease;
    /** Opened from a spot: the object is pre-filled and locked to that spot. */
    lockedSpot?: { areaKey: string; spotKey: string };
    /** Opened from a storage unit: the object is pre-filled and locked to that unit. */
    lockedUnit?: { unitKey: string };
    /** Opened from a lägenhet or lokal: the object is pre-filled and locked to that unit. */
    lockedRentalUnit?: { unitKey: string };
    /** Create only: pre-selected tenant (the parking queue's applicant). */
    initialTenantKey?: string;
    /** Create only: pre-filled start date, YYYY-MM-DD (default today). */
    initialStartDate?: string;
    submitting?: boolean;
    /**
     * Hyresavtal (/leases): the Byggnad field, first in the form. `buildingKey`
     * is then the chosen building ('' while none); choosing one clears the
     * picked object. Read-only when editing, since a lease never changes building.
     */
    buildingChoice?: {
        buildings: { key: string; name: string }[];
        onChange: (buildingKey: string) => void;
        /** The chosen building's objects are loading. */
        loading?: boolean;
        error?: string | null;
    };
}

/**
 * Picker choices. Apartment and commercial unit are one choice (`unit`); the
 * exact lease object type is set from the picked unit's kind (plan D7).
 */
type ObjectChoice = LeaseObjectType.ParkingSpot | LeaseObjectType.StorageUnit | 'unit' | LeaseObjectType.Other;
const OBJECT_CHOICES: { value: ObjectChoice; label: string }[] = [
    { value: LeaseObjectType.ParkingSpot, label: 'Parkeringsplats' },
    { value: LeaseObjectType.StorageUnit, label: 'Förråd' },
    { value: 'unit', label: 'Lägenhet/lokal' },
    { value: LeaseObjectType.Other, label: 'Annat' },
];
const OBJECT_CHOICE_VALUES = OBJECT_CHOICES.map(choice => choice.value);
const choiceForType = (type: LeaseObjectType): ObjectChoice =>
    UNIT_LEASE_OBJECT_TYPES.includes(type) ? 'unit' : (type as ObjectChoice);
/** Choices of features turned off are not offered (Inställningar → Funktioner); null = loading, all on. */
const choiceEnabled = (choice: ObjectChoice, features: Features | null): boolean =>
    !features
    || (choice === LeaseObjectType.ParkingSpot ? features.parking
        : choice === LeaseObjectType.StorageUnit ? features.storageUnits
        : choice === 'unit' ? features.units
        : true);

/** One row in "Fler personer på avtalet"; `id` is a stable React key, never stored. */
interface CoTenantRow extends CoTenantEntry {
    id: number;
}

let nextCoTenantRowId = 1;
const coTenantRow = (entry: CoTenantEntry): CoTenantRow => ({ ...entry, id: nextCoTenantRowId++ });
const EMPTY_CO_TENANT: CoTenantEntry = { tenantKey: '', role: CoTenantRole.ContractHolder };

interface LeaseFormValues {
    tenantKey: string;
    coTenants: CoTenantRow[];
    objectType: ObjectChoice;
    areaKey: string;
    spotKey: string;
    unitKey: string;
    rentalUnitKey: string;
    label: string;
    amount: string;
    intervalPreset: string;
    customCount: string;
    customUnit: IntervalUnit;
    startDate: string;
    endDate: string;
    comment: string;
    /** Terms (phase 3), shown for lokal leases. */
    noticeMonths: string;
    renewalMonths: string;
    indexClause: boolean;
    vatRegistered: boolean;
    /** Edit only: the date a changed amount applies from. */
    amountEffectiveFrom: string;
}

const CUSTOM = 'Anpassad';
const INTERVAL_PRESETS = [
    { label: 'Månadsvis', count: 1, unit: 'month' as IntervalUnit },
    { label: 'Kvartalsvis', count: 3, unit: 'month' as IntervalUnit },
    { label: 'Halvårsvis', count: 6, unit: 'month' as IntervalUnit },
    { label: 'Årsvis', count: 1, unit: 'year' as IntervalUnit },
    { label: 'Vartannat år', count: 2, unit: 'year' as IntervalUnit },
    { label: CUSTOM, count: 0, unit: 'month' as IntervalUnit },
];

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif'];

const schema = Yup.object({
    tenantKey: Yup.string().required('Välj person'),
    coTenants: Yup.array().of(Yup.object({
        tenantKey: Yup.string().required('Välj person')
            .test('not-primary', 'Samma person som kontraktsinnehavaren', function (value) {
                // this.from[1] is the form root (Yup nests the row under the array).
                const root = this.from?.[1]?.value as LeaseFormValues | undefined;
                return !value || !root || value !== root.tenantKey;
            })
            .test('unique', 'Personen finns redan på avtalet', function (value) {
                const root = this.from?.[1]?.value as LeaseFormValues | undefined;
                return !value || !root || root.coTenants.filter(row => row.tenantKey === value).length <= 1;
            }),
        role: Yup.mixed<CoTenantRole>().oneOf(Object.values(CoTenantRole)).required(),
    })).defined(),
    objectType: Yup.mixed<ObjectChoice>().oneOf(OBJECT_CHOICE_VALUES).required(),
    areaKey: Yup.string().when('objectType', { is: LeaseObjectType.ParkingSpot, then: s => s.required('Välj parkeringsområde') }),
    spotKey: Yup.string().when('objectType', { is: LeaseObjectType.ParkingSpot, then: s => s.required('Välj plats') }),
    unitKey: Yup.string().when('objectType', { is: LeaseObjectType.StorageUnit, then: s => s.required('Välj förråd') }),
    rentalUnitKey: Yup.string().when('objectType', { is: 'unit', then: s => s.required('Välj lägenhet eller lokal') }),
    label: Yup.string().trim().max(200, 'Max 200 tecken').when('objectType', { is: LeaseObjectType.Other, then: s => s.required('Beskriv hyresobjektet') }),
    amount: Yup.number().typeError('Ange ett belopp').min(0, 'Beloppet kan inte vara negativt').required('Belopp krävs'),
    intervalPreset: Yup.string().required(),
    customCount: Yup.number().when('intervalPreset', { is: CUSTOM, then: s => s.typeError('Ange antal').integer('Heltal').min(1, 'Minst 1').required('Ange antal') }),
    customUnit: Yup.mixed<IntervalUnit>().oneOf(['month', 'year']),
    startDate: Yup.string().matches(DATE_ONLY_RE, 'Ogiltigt datum').required('Startdatum krävs'),
    endDate: Yup.string().matches(DATE_ONLY_RE, 'Ogiltigt datum').test(
        'after-start', 'Slutdatum måste vara efter startdatum',
        function (value) { return !value || !this.parent.startDate || value >= this.parent.startDate; }
    ),
    comment: Yup.string().trim().max(500, 'Max 500 tecken'),
    noticeMonths: Yup.string().matches(/^\d*$/, 'Ange hela månader'),
    renewalMonths: Yup.string().matches(/^\d*$/, 'Ange hela månader'),
    amountEffectiveFrom: Yup.string().matches(DATE_ONLY_RE, 'Ogiltigt datum'),
});

const presetFor = (count: number, unit: IntervalUnit): string =>
    INTERVAL_PRESETS.find(p => p.label !== CUSTOM && p.count === count && p.unit === unit)?.label ?? CUSTOM;

/** The picked object, matched against earlier leases; null while none is picked (and for Annat). */
const objectRefFor = (values: LeaseFormValues): LeasedObjectRef | null => {
    if (values.objectType === LeaseObjectType.ParkingSpot) {
        return values.areaKey && values.spotKey ? { type: LeaseObjectType.ParkingSpot, areaKey: values.areaKey, key: values.spotKey } : null;
    }
    if (values.objectType === LeaseObjectType.StorageUnit) return values.unitKey ? { type: LeaseObjectType.StorageUnit, key: values.unitKey } : null;
    if (values.objectType === 'unit') return values.rentalUnitKey ? { type: 'unit', key: values.rentalUnitKey } : null;
    return null;
};

type RentValues = Pick<LeaseFormValues, 'amount' | 'intervalPreset' | 'customCount' | 'customUnit'>;
const RENT_FIELDS: (keyof RentValues)[] = ['amount', 'intervalPreset', 'customCount', 'customUnit'];
const EMPTY_RENT: RentValues = { amount: '', intervalPreset: 'Månadsvis', customCount: '1', customUnit: 'month' };
const sameRent = (a: RentValues, b: RentValues): boolean => RENT_FIELDS.every(field => a[field] === b[field]);

/**
 * Create only: picking an object fills in the rent and interval of its latest
 * lease, and leaves them empty when it was never rented. Rent the user typed
 * is kept; only a value this filled in is replaced on the next pick.
 */
const PreviousRentPrefill: React.FC<{ buildingLeases: Lease[] }> = ({ buildingLeases }) => {
    const { values, setValues } = useFormikContext<LeaseFormValues>();
    const ref = objectRefFor(values);
    const previous = ref ? latestLeaseForObject(buildingLeases, ref) : null;
    const prefilled = useRef<RentValues>(EMPTY_RENT);
    const objectId = ref ? `${ref.type}/${'areaKey' in ref ? ref.areaKey : ''}/${ref.key}` : '';
    useEffect(() => {
        const current: RentValues = { amount: values.amount, intervalPreset: values.intervalPreset, customCount: values.customCount, customUnit: values.customUnit };
        if (!sameRent(current, prefilled.current) && !sameRent(current, EMPTY_RENT)) return;
        const next: RentValues = previous
            ? {
                amount: String(previous.amount),
                intervalPreset: presetFor(previous.interval.count, previous.interval.unit),
                customCount: String(previous.interval.count),
                customUnit: previous.interval.unit,
            }
            : EMPTY_RENT;
        prefilled.current = next;
        if (!sameRent(current, next)) setValues(v => ({ ...v, ...next }), false);
        // Runs per picked object (and once its leases have loaded), not per keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectId, previous?.key]);
    return null;
};

const toValues = (
    lease: Lease | undefined,
    lockedSpot: LeaseModalProps['lockedSpot'],
    lockedUnit: LeaseModalProps['lockedUnit'],
    lockedRentalUnit: LeaseModalProps['lockedRentalUnit'],
    initialTenantKey?: string,
    initialStartDate?: string,
    newLeaseChoice: ObjectChoice = LeaseObjectType.ParkingSpot
): LeaseFormValues => ({
    tenantKey: lease?.tenantKey ?? initialTenantKey ?? '',
    coTenants: lease ? coTenantEntries(lease).map(coTenantRow) : [],
    objectType: lease
        ? choiceForType(lease.object.type)
        : lockedRentalUnit ? 'unit' : lockedUnit ? LeaseObjectType.StorageUnit : lockedSpot ? LeaseObjectType.ParkingSpot : newLeaseChoice,
    areaKey: (lease?.object.type === LeaseObjectType.ParkingSpot ? lease.object.areaKey : undefined) ?? lockedSpot?.areaKey ?? '',
    spotKey: (lease?.object.type === LeaseObjectType.ParkingSpot ? lease.object.key : undefined) ?? lockedSpot?.spotKey ?? '',
    unitKey: (lease?.object.type === LeaseObjectType.StorageUnit ? lease.object.key : undefined) ?? lockedUnit?.unitKey ?? '',
    rentalUnitKey: (lease && UNIT_LEASE_OBJECT_TYPES.includes(lease.object.type) ? lease.object.key : undefined) ?? lockedRentalUnit?.unitKey ?? '',
    label: lease?.object.type === LeaseObjectType.Other ? (lease.object.label ?? '') : '',
    amount: lease ? String(lease.amount) : '',
    intervalPreset: lease ? presetFor(lease.interval.count, lease.interval.unit) : 'Månadsvis',
    customCount: lease ? String(lease.interval.count) : '1',
    customUnit: lease?.interval.unit ?? 'month',
    startDate: lease?.startDate ?? initialStartDate ?? todayStockholm(),
    endDate: lease?.endDate ?? '',
    comment: lease?.comment ?? '',
    noticeMonths: lease?.noticeMonths ? String(lease.noticeMonths) : '',
    renewalMonths: lease?.renewalMonths ? String(lease.renewalMonths) : '',
    indexClause: lease?.indexClause === true,
    vatRegistered: lease?.vatRegistered === true,
    // A rent change applies from today at the earliest, but never before the lease starts.
    amountEffectiveFrom: lease && lease.startDate > todayStockholm() ? lease.startDate : todayStockholm(),
});

const toInput = (values: LeaseFormValues, buildingKey: string, rentalUnits: Unit[], existing?: Lease): LeaseInput => {
    const preset = INTERVAL_PRESETS.find(p => p.label === values.intervalPreset);
    const interval = preset && preset.label !== CUSTOM
        ? { count: preset.count, unit: preset.unit }
        : { count: parseInt(values.customCount, 10), unit: values.customUnit };
    const coTenants: CoTenantsMap = {};
    for (const row of values.coTenants) {
        if (row.tenantKey) coTenants[row.tenantKey] = { role: row.role };
    }
    return {
        tenantKey: values.tenantKey,
        coTenants: Object.keys(coTenants).length > 0 ? coTenants : undefined,
        buildingKey,
        object: values.objectType === LeaseObjectType.ParkingSpot
            ? { type: LeaseObjectType.ParkingSpot, areaKey: values.areaKey, key: values.spotKey }
            : values.objectType === LeaseObjectType.StorageUnit
                ? { type: LeaseObjectType.StorageUnit, key: values.unitKey }
                : values.objectType === 'unit'
                    // The API re-derives the type from the unit node; this is the best local guess.
                    ? { type: leaseObjectTypeForUnitKind(rentalUnits.find(u => u.key === values.rentalUnitKey)?.kind ?? UnitKind.Apartment), key: values.rentalUnitKey }
                    : { type: LeaseObjectType.Other, label: values.label.trim() },
        amount: parseFloat(values.amount),
        interval,
        startDate: values.startDate,
        endDate: values.endDate || undefined,
        comment: values.comment.trim() || undefined,
        noticeMonths: values.noticeMonths ? parseInt(values.noticeMonths, 10) : undefined,
        renewalMonths: values.renewalMonths ? parseInt(values.renewalMonths, 10) : undefined,
        indexClause: values.indexClause || undefined,
        vatRegistered: values.vatRegistered || undefined,
        amountEffectiveFrom: existing && parseFloat(values.amount) !== existing.amount ? values.amountEffectiveFrom : undefined,
    };
};

/**
 * Create/edit a lease. Formik + Yup per web/CLAUDE.md. Contract files are
 * collected here and uploaded by the page after the lease exists.
 */
const LeaseModal: React.FC<LeaseModalProps> = ({
    show, onHide, onSubmit, buildingKey, areas, units = [], levels = {}, rentalUnits = [], buildingLeases, lease, lockedSpot, lockedUnit, lockedRentalUnit, initialTenantKey, initialStartDate, submitting = false, buildingChoice
}) => {
    const tenantTerm = useTenantTerm();
    const objectLocked = !!lockedSpot || !!lockedUnit || !!lockedRentalUnit;
    // An edited lease keeps its own choice even when that feature is off.
    const features = useFeatures();
    const objectChoices = OBJECT_CHOICES.filter(choice =>
        choiceEnabled(choice.value, features) || (lease && choiceForType(lease.object.type) === choice.value));
    const isEditing = !!lease;
    // Opened from a spot or unit panel: the object is known, so it is shown as
    // text instead of a disabled picker.
    const lockedObjectLabel = useMemo(() => {
        if (lockedSpot) {
            const area = areas.find(a => a.key === lockedSpot.areaKey);
            const spot = area?.spots?.[lockedSpot.spotKey];
            return parkingSpotLeaseLabel(area ? parkingAreaDisplayName(area) : '', spot?.number ?? '');
        }
        if (lockedUnit) {
            const unit = units.find(u => u.key === lockedUnit.unitKey);
            const level = unit ? levels[unit.levelKey] : undefined;
            return storageUnitLeaseLabel(level ? levelDisplayName(level) : '', unit?.number ?? '');
        }
        if (lockedRentalUnit) {
            const unit = rentalUnits.find(u => u.key === lockedRentalUnit.unitKey);
            return unitLeaseLabel(unit?.kind ?? UnitKind.Apartment, unit?.number ?? '');
        }
        return null;
    }, [lockedSpot, lockedUnit, lockedRentalUnit, areas, units, levels, rentalUnits]);
    const { tenants, allTenants, getTenantName, refresh: refreshTenants } = useTenants();
    // Where a tenant created inline goes: the primary field or the co-tenant list.
    const [tenantModalTarget, setTenantModalTarget] = useState<'primary' | 'coTenant' | null>(null);
    // The inline "lägg till person" picker; null while hidden.
    const [pendingCoTenant, setPendingCoTenant] = useState<CoTenantEntry | null>(null);
    const [creatingTenant, setCreatingTenant] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [contractsToRemove, setContractsToRemove] = useState<string[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);

    const leasesByKey = useMemo(() => {
        const map: Record<string, Lease> = {};
        for (const l of buildingLeases) map[l.key] = l;
        return map;
    }, [buildingLeases]);

    const rentedSuffix = useCallback((currentLeaseKey: string | undefined, vacantWord: string) => {
        if (!currentLeaseKey) return vacantWord;
        if (currentLeaseKey === lease?.key) return 'detta avtal';
        const holder = leasesByKey[currentLeaseKey];
        return holder?.endDate ? `uthyrd t.o.m. ${holder.endDate}` : 'uthyrd';
    }, [leasesByKey, lease]);

    const spotOptionLabel = useCallback((spot: ParkingSpot) =>
        `${parkingSpotDisplayName(spot)} · ${rentedSuffix(spot.currentLeaseKey, 'ledig')}`, [rentedSuffix]);

    const unitOptionLabel = useCallback((unit: StorageUnit) =>
        `${storageUnitDisplayName(unit)} · ${rentedSuffix(unit.currentLeaseKey, 'ledigt')}`, [rentedSuffix]);

    const rentalUnitOptionLabel = useCallback((unit: Unit) =>
        `${unitDisplayName(unit)} · ${rentedSuffix(unit.currentLeaseKey, 'ledig')}`, [rentedSuffix]);

    // Lägenhet/lokal picker: hyresrätt units only (plan D3), plus the unit the edited lease already holds; grouped by kind.
    const rentalUnitGroups = useMemo(() => {
        const offered = rentalUnits.filter(unit => isUnitLeasable(unit) || (lease && lease.object.key === unit.key));
        return [
            { key: UnitKind.Apartment, label: 'Lägenheter', units: offered.filter(unit => unit.kind === UnitKind.Apartment) },
            { key: UnitKind.Commercial, label: 'Lokaler', units: offered.filter(unit => unit.kind === UnitKind.Commercial) },
        ].filter(group => group.units.length > 0);
    }, [rentalUnits, lease]);

    // Unit picker grouped by level (highest first); units whose level is gone end up last.
    const unitGroups = useMemo(() => {
        const groups = storageLevelsDescending(levels)
            .map(level => ({ key: level.key, label: levelDisplayName(level), units: unitsOnLevel(units, level.key) }))
            .filter(group => group.units.length > 0);
        const known = new Set(groups.map(g => g.key));
        const orphans = units.filter(unit => !known.has(unit.levelKey));
        if (orphans.length > 0) groups.push({ key: '', label: 'Utan våning', units: orphans });
        return groups;
    }, [levels, units]);

    const handleFiles = useCallback((files: File[]) => {
        const valid = files.filter(file => ALLOWED_EXTENSIONS.includes('.' + (file.name.split('.').pop() || '').toLowerCase()));
        if (valid.length === 0 && files.length > 0) {
            setFileError('Endast PDF, Word, Excel eller bilder är tillåtna');
            return;
        }
        setFileError(null);
        setPendingFiles(prev => [...prev, ...valid]);
    }, []);
    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });

    const existingContracts: FileAttachment[] = Object.values(lease?.contracts || {});

    const handleSubmit = async (values: LeaseFormValues, helpers: FormikHelpers<LeaseFormValues>) => {
        if (!buildingKey) {
            helpers.setStatus('Välj byggnad');
            return;
        }
        try {
            await onSubmit({ input: toInput(values, buildingKey, rentalUnits, lease), filesToUpload: pendingFiles, contractsToRemove });
            setPendingFiles([]);
            setContractsToRemove([]);
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte spara hyresavtalet');
        }
    };

    const handleHide = () => {
        setPendingCoTenant(null);
        setPendingFiles([]);
        setContractsToRemove([]);
        setFileError(null);
        onHide();
    };

    return (
        <>
            <Modal show={show} onHide={handleHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Redigera hyresavtal' : 'Nytt hyresavtal'}</Modal.Title>
                </Modal.Header>
                <Formik<LeaseFormValues>
                    initialValues={toValues(lease, lockedSpot, lockedUnit, lockedRentalUnit, initialTenantKey, initialStartDate, objectChoices[0].value)}
                    validationSchema={schema}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, status, handleChange, handleBlur, handleSubmit: submitForm, setFieldValue, setStatus }) => {
                        const area = areas.find(a => a.key === values.areaKey);
                        const spots = area ? sortedParkingSpots(area) : [];
                        // Contract terms are a lokal thing (phase 3): shown for commercial unit leases, or whenever terms are already set.
                        const pickedUnitKey = values.objectType === 'unit' ? (values.rentalUnitKey || lockedRentalUnit?.unitKey) : undefined;
                        const pickedUnit = pickedUnitKey ? rentalUnits.find(u => u.key === pickedUnitKey) : undefined;
                        const showTerms = pickedUnit?.kind === UnitKind.Commercial
                            || lease?.object.type === LeaseObjectType.CommercialUnit
                            || !!(lease && (lease.noticeMonths || lease.renewalMonths || lease.indexClause || lease.vatRegistered));
                        const amountChanged = !!lease && values.amount !== '' && parseFloat(values.amount) !== lease.amount;
                        const createTenant = async (input: TenantInput) => {
                            setCreatingTenant(true);
                            try {
                                const tenant = await tenantsApi.create(input);
                                await refreshTenants();
                                if (tenantModalTarget === 'primary') {
                                    setFieldValue('tenantKey', tenant.key);
                                } else if (tenantModalTarget === 'coTenant') {
                                    addCoTenant({ tenantKey: tenant.key, role: pendingCoTenant?.role ?? CoTenantRole.ContractHolder });
                                }
                                setTenantModalTarget(null);
                            } finally {
                                setCreatingTenant(false);
                            }
                        };
                        const tenantOption = (t: Tenant) => (
                            <option key={t.key} value={t.key}>
                                {t.name}{t.kind === TenantKind.Company && t.orgNumber ? ` (${t.orgNumber})` : ''}
                            </option>
                        );
                        const usedKeys = new Set([values.tenantKey, ...values.coTenants.map(row => row.tenantKey)]);
                        const coTenantErrors = errors.coTenants as Array<{ tenantKey?: string } | undefined> | undefined;
                        // People are added as finished rows, so a row never has an empty person;
                        // the only row error left is the primary being picked twice.
                        const addCoTenant = (entry: CoTenantEntry) => {
                            setFieldValue('coTenants', [...values.coTenants, coTenantRow(entry)]);
                            setPendingCoTenant(null);
                        };
                        const removeCoTenant = (index: number) =>
                            setFieldValue('coTenants', values.coTenants.filter((_, i) => i !== index));
                        return (
                            <Form noValidate onSubmit={submitForm}>
                                {!isEditing && <PreviousRentPrefill buildingLeases={buildingLeases} />}
                                <Modal.Body>
                                    {status && <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>}

                                    {buildingChoice && (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Byggnad *</Form.Label>
                                            {isEditing ? (
                                                <div className="form-control-plaintext">
                                                    {buildingChoice.buildings.find(b => b.key === buildingKey)?.name || 'Okänd byggnad'}
                                                </div>
                                            ) : (
                                                <Form.Select
                                                    value={buildingKey}
                                                    onChange={e => {
                                                        // The picked object belongs to the old building.
                                                        setFieldValue('areaKey', '');
                                                        setFieldValue('spotKey', '');
                                                        setFieldValue('unitKey', '');
                                                        setFieldValue('rentalUnitKey', '');
                                                        buildingChoice.onChange(e.target.value);
                                                    }}
                                                    disabled={submitting}
                                                >
                                                    <option value="">Välj byggnad…</option>
                                                    {buildingChoice.buildings.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
                                                </Form.Select>
                                            )}
                                            {buildingChoice.loading && <Form.Text className="text-muted"><Spinner size="sm" className="me-1" />Laddar byggnadens hyresobjekt…</Form.Text>}
                                            {buildingChoice.error && <div className="invalid-feedback d-block">{buildingChoice.error}</div>}
                                        </Form.Group>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Label>{tenantTerm.singular} *</Form.Label>
                                        <div className="d-flex gap-2">
                                            <Form.Select
                                                name="tenantKey"
                                                value={values.tenantKey}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.tenantKey && !!errors.tenantKey}
                                            >
                                                <option value="">Välj {tenantTerm.singular.toLowerCase()}…</option>
                                                {/* The current tenant stays selectable even when anonymised (an ended lease being edited). */}
                                                {allTenants.filter(t => t.key === values.tenantKey || (!t.anonymized && !usedKeys.has(t.key))).map(tenantOption)}
                                            </Form.Select>
                                            <Button variant="outline-secondary" onClick={() => setTenantModalTarget('primary')} title={`Ny ${tenantTerm.singular.toLowerCase()}`}>
                                                <Plus size={16} />
                                            </Button>
                                        </div>
                                        {touched.tenantKey && errors.tenantKey && <div className="invalid-feedback d-block">{errors.tenantKey}</div>}
                                        <Form.Text className="text-muted">Kontraktsinnehavare: den som får fakturan och är kontaktperson.</Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <div className="d-flex align-items-center gap-3 mb-2">
                                            <Form.Label className="mb-0">Fler personer på avtalet</Form.Label>
                                            {!pendingCoTenant && (
                                                <Button variant="outline-secondary" size="sm" onClick={() => setPendingCoTenant(EMPTY_CO_TENANT)} disabled={submitting}>
                                                    <Plus size={14} className="me-1" />Lägg till person
                                                </Button>
                                            )}
                                        </div>

                                        {values.coTenants.map((row, index) => {
                                            const rowError = coTenantErrors?.[index]?.tenantKey;
                                            return (
                                                <div key={row.id} className="mb-1">
                                                    <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                                                        <User size={16} className="text-muted" />
                                                        <span className="flex-grow-1 text-truncate" style={{ fontSize: '14px' }}>{getTenantName(row.tenantKey)}</span>
                                                        <Form.Select
                                                            size="sm"
                                                            className="w-auto"
                                                            name={`coTenants.${index}.role`}
                                                            value={row.role}
                                                            onChange={handleChange}
                                                            disabled={submitting}
                                                            aria-label="Roll"
                                                        >
                                                            {Object.values(CoTenantRole).map(role => <option key={role} value={role}>{translate(role)}</option>)}
                                                        </Form.Select>
                                                        <Button variant="link" size="sm" className="p-0 text-danger" title="Ta bort från avtalet" disabled={submitting} onClick={() => removeCoTenant(index)}><X size={16} /></Button>
                                                    </div>
                                                    {rowError && <div className="invalid-feedback d-block">{rowError}</div>}
                                                </div>
                                            );
                                        })}

                                        {pendingCoTenant && (
                                            <div className="d-flex gap-2 mb-1">
                                                <Form.Select
                                                    value={pendingCoTenant.tenantKey}
                                                    onChange={e => setPendingCoTenant({ ...pendingCoTenant, tenantKey: e.target.value })}
                                                    aria-label="Person"
                                                    autoFocus
                                                >
                                                    <option value="">Välj person…</option>
                                                    {tenants.filter(t => !usedKeys.has(t.key)).map(tenantOption)}
                                                </Form.Select>
                                                <Form.Select
                                                    className="w-auto"
                                                    value={pendingCoTenant.role}
                                                    onChange={e => setPendingCoTenant({ ...pendingCoTenant, role: e.target.value as CoTenantRole })}
                                                    aria-label="Roll"
                                                >
                                                    {Object.values(CoTenantRole).map(role => <option key={role} value={role}>{translate(role)}</option>)}
                                                </Form.Select>
                                                <Button variant="outline-secondary" onClick={() => setTenantModalTarget('coTenant')} title="Ny person">
                                                    <Plus size={16} />
                                                </Button>
                                                <Button variant="primary" disabled={!pendingCoTenant.tenantKey} onClick={() => addCoTenant(pendingCoTenant)}>Lägg till</Button>
                                                <Button variant="link" className="text-secondary px-1" onClick={() => setPendingCoTenant(null)}>Avbryt</Button>
                                            </div>
                                        )}

                                        <Form.Text className="text-muted d-block mt-1">
                                            Medhyresgäst står på avtalet och delar betalningsansvaret. Medboende bor i objektet utan att stå på avtalet.
                                        </Form.Text>
                                    </Form.Group>

                                    {objectLocked ? (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Hyresobjekt</Form.Label>
                                            <div className="form-control-plaintext">{lockedObjectLabel}</div>
                                        </Form.Group>
                                    ) : (
                                        <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Hyresobjekt *</Form.Label>
                                        <div className="d-flex flex-wrap column-gap-3">
                                            {objectChoices.map(choice => (
                                                <Form.Check
                                                    key={choice.value}
                                                    type="radio"
                                                    id={`lease-object-${choice.value}`}
                                                    name="objectType"
                                                    value={choice.value}
                                                    label={choice.label}
                                                    checked={values.objectType === choice.value}
                                                    onChange={handleChange}
                                                    disabled={objectLocked}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>

                                    {values.objectType === LeaseObjectType.ParkingSpot ? (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Parkeringsområde *</Form.Label>
                                                    <Form.Select
                                                        name="areaKey"
                                                        value={values.areaKey}
                                                        onChange={e => { setFieldValue('areaKey', e.target.value); setFieldValue('spotKey', ''); }}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.areaKey && !!errors.areaKey}
                                                    >
                                                        <option value="">Välj område…</option>
                                                        {areas.map(a => (
                                                            <option key={a.key} value={a.key}>{parkingAreaDisplayName(a)}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">{errors.areaKey}</Form.Control.Feedback>
                                                </Form.Group>
                                            </div>
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Plats *</Form.Label>
                                                    <Form.Select
                                                        name="spotKey"
                                                        value={values.spotKey}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        disabled={!area}
                                                        isInvalid={touched.spotKey && !!errors.spotKey}
                                                    >
                                                        <option value="">{area ? 'Välj plats…' : 'Välj område först'}</option>
                                                        {spots.map(spot => (
                                                            <option key={spot.key} value={spot.key}>{spotOptionLabel(spot)}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">{errors.spotKey}</Form.Control.Feedback>
                                                </Form.Group>
                                            </div>
                                        </div>
                                    ) : values.objectType === LeaseObjectType.StorageUnit ? (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Förråd *</Form.Label>
                                            <Form.Select
                                                name="unitKey"
                                                value={values.unitKey}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.unitKey && !!errors.unitKey}
                                            >
                                                <option value="">{unitGroups.length > 0 ? 'Välj förråd…' : 'Inga förråd registrerade'}</option>
                                                {unitGroups.map(group => (
                                                    <optgroup key={group.key || 'orphans'} label={group.label}>
                                                        {group.units.map(unit => (
                                                            <option key={unit.key} value={unit.key}>{unitOptionLabel(unit)}</option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.unitKey}</Form.Control.Feedback>
                                        </Form.Group>
                                    ) : values.objectType === 'unit' ? (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Lägenhet eller lokal *</Form.Label>
                                            <Form.Select
                                                name="rentalUnitKey"
                                                value={values.rentalUnitKey}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={touched.rentalUnitKey && !!errors.rentalUnitKey}
                                            >
                                                <option value="">{rentalUnitGroups.length > 0 ? 'Välj lägenhet eller lokal…' : 'Inga hyresrätter registrerade'}</option>
                                                {rentalUnitGroups.map(group => (
                                                    <optgroup key={group.key} label={group.label}>
                                                        {group.units.map(unit => (
                                                            <option key={unit.key} value={unit.key}>{rentalUnitOptionLabel(unit)}</option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </Form.Select>
                                            <Form.Text muted>Endast lägenheter och lokaler med hyresrätt kan hyras ut.</Form.Text>
                                            <Form.Control.Feedback type="invalid">{errors.rentalUnitKey}</Form.Control.Feedback>
                                        </Form.Group>
                                    ) : (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Beskrivning av hyresobjektet *</Form.Label>
                                            <Form.Control
                                                name="label"
                                                value={values.label}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="T.ex. Antennplats tak"
                                                isInvalid={touched.label && !!errors.label}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.label}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                        </>
                                    )}

                                    <div className="row">
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Belopp (SEK) *</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    name="amount"
                                                    value={values.amount}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="0"
                                                    isInvalid={touched.amount && !!errors.amount}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Betalningsintervall *</Form.Label>
                                                <Form.Select name="intervalPreset" value={values.intervalPreset} onChange={handleChange}>
                                                    {INTERVAL_PRESETS.map(preset => (
                                                        <option key={preset.label} value={preset.label}>{preset.label}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                    </div>

                                    {values.intervalPreset === CUSTOM && (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Var</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        name="customCount"
                                                        value={values.customCount}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.customCount && !!errors.customCount}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.customCount}</Form.Control.Feedback>
                                                </Form.Group>
                                            </div>
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Enhet</Form.Label>
                                                    <Form.Select name="customUnit" value={values.customUnit} onChange={handleChange}>
                                                        <option value="month">månad</option>
                                                        <option value="year">år</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row">
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Startdatum *</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="startDate"
                                                    value={values.startDate}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={touched.startDate && !!errors.startDate}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Slutdatum</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="endDate"
                                                    value={values.endDate}
                                                    min={values.startDate || undefined}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={touched.endDate && !!errors.endDate}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
                                                <Form.Text className="text-muted">Lämna tomt för tills vidare.</Form.Text>
                                            </Form.Group>
                                        </div>
                                    </div>

                                    {amountChanged && (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Ny hyra gäller från *</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="amountEffectiveFrom"
                                                        value={values.amountEffectiveFrom}
                                                        min={values.startDate || undefined}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.amountEffectiveFrom && !!errors.amountEffectiveFrom}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.amountEffectiveFrom}</Form.Control.Feedback>
                                                    <Form.Text className="text-muted">Den gamla hyran sparas i hyreshistoriken fram till detta datum.</Form.Text>
                                                </Form.Group>
                                            </div>
                                        </div>
                                    )}

                                    {showTerms && (
                                        <>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Uppsägningstid (månader)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            name="noticeMonths"
                                                            value={values.noticeMonths}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="T.ex. 9"
                                                            isInvalid={touched.noticeMonths && !!errors.noticeMonths}
                                                        />
                                                        <Form.Control.Feedback type="invalid">{errors.noticeMonths}</Form.Control.Feedback>
                                                        <Form.Text className="text-muted">Ger datumet "Uppsägning senast" när avtalet har ett slutdatum.</Form.Text>
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-6">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Förlängningstid (månader)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            name="renewalMonths"
                                                            value={values.renewalMonths}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="T.ex. 36"
                                                            isInvalid={touched.renewalMonths && !!errors.renewalMonths}
                                                        />
                                                        <Form.Control.Feedback type="invalid">{errors.renewalMonths}</Form.Control.Feedback>
                                                    </Form.Group>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-4 mb-3">
                                                <Form.Check
                                                    type="checkbox"
                                                    id="lease-index-clause"
                                                    name="indexClause"
                                                    label="Indexklausul (KPI)"
                                                    checked={values.indexClause}
                                                    onChange={handleChange}
                                                />
                                                <Form.Check
                                                    type="checkbox"
                                                    id="lease-vat-registered"
                                                    name="vatRegistered"
                                                    label="Moms (frivillig skattskyldighet)"
                                                    checked={values.vatRegistered}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Label>Kommentar</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="comment"
                                            value={values.comment}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            isInvalid={touched.comment && !!errors.comment}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.comment}</Form.Control.Feedback>
                                    </Form.Group>

                                    <div
                                        {...dragHandlers}
                                        style={{
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                            backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-3 mb-2">
                                            <Form.Label className="mb-0">Avtal och bilagor</Form.Label>
                                            <Button variant="outline-secondary" size="sm" onClick={() => setShowUploadModal(true)} disabled={submitting}>
                                                <Upload size={14} className="me-1" />Lägg till fil
                                            </Button>
                                        </div>
                                        {fileError && <Alert variant="warning" className="py-1 px-2 mb-2" dismissible onClose={() => setFileError(null)}>{fileError}</Alert>}

                                        {existingContracts.map(contract => {
                                            const removed = contractsToRemove.includes(contract.key);
                                            return (
                                                <div key={contract.key} className={`d-flex align-items-center gap-2 p-2 bg-light rounded mb-1 ${removed ? 'opacity-50' : ''}`}>
                                                    <FileText size={16} className="text-muted" />
                                                    <span className={`flex-grow-1 text-truncate ${removed ? 'text-decoration-line-through' : ''}`} style={{ fontSize: '14px' }}>{contract.fileName}</span>
                                                    <span className="text-muted" style={{ fontSize: '12px' }}>{formatFileSize(contract.fileSize)}</span>
                                                    {removed ? (
                                                        <Button variant="link" size="sm" className="p-0 text-primary" onClick={() => setContractsToRemove(prev => prev.filter(k => k !== contract.key))}>Ångra</Button>
                                                    ) : (
                                                        <Button variant="link" size="sm" className="p-0 text-danger" title="Ta bort" onClick={() => setContractsToRemove(prev => [...prev, contract.key])}><X size={16} /></Button>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {pendingFiles.map((file, index) => (
                                            <div key={`pending-${index}`} className="d-flex align-items-center gap-2 p-2 bg-success bg-opacity-10 rounded mb-1">
                                                <Upload size={16} className="text-success" />
                                                <span className="flex-grow-1 text-truncate" style={{ fontSize: '14px' }}>{file.name}</span>
                                                <span className="text-muted" style={{ fontSize: '12px' }}>{formatFileSize(file.size)}</span>
                                                <span className="badge bg-success" style={{ fontSize: '10px' }}>Ny</span>
                                                <Button variant="link" size="sm" className="p-0 text-danger" title="Ta bort" onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== index))}><X size={16} /></Button>
                                            </div>
                                        ))}

                                        <Form.Text className="text-muted d-block mt-1">
                                            PDF, Word, Excel eller bilder. Du kan också dra och släppa filer här.
                                        </Form.Text>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={handleHide} disabled={submitting}>Avbryt</Button>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting ? <><Spinner size="sm" className="me-2" />{isEditing ? 'Sparar...' : 'Skapar...'}</> : (isEditing ? 'Spara' : 'Skapa avtal')}
                                    </Button>
                                </Modal.Footer>

                                <TenantModal
                                    show={tenantModalTarget !== null}
                                    onHide={() => setTenantModalTarget(null)}
                                    onSubmit={createTenant}
                                    submitting={creatingTenant}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>

            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                accept={ALLOWED_EXTENSIONS.join(',')}
                multiple
                title="Ladda upp avtal"
            />
        </>
    );
};

export default LeaseModal;
